/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type { FC, PropsWithChildren } from 'react';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { RenderMode } from '@oneaudi/render-mode-service';
import type { MoxxCarlineGroup } from '../../@types/MoxxTypes';
import type { ContextProviderProps, ContextState } from './ContextTypes';
import type { Content } from '../../@types/EditorContentTypes';
import type { I18nMessages } from '../../@types/FeatureAppTypes';
import type { CarlineGroup } from '../../web/generated/onegraph';
import { mapHeadlessContent } from '../utils/mapHeadlessContent';
import { ALL_MODELS } from '../constants';

let i18nMessages!: I18nMessages;
export const Context = createContext<ContextState>({} as ContextState);

const hasShowBasePrice = (content: Content) => content.priceDisplayOption.showBasePrice;

const hasShowVat = (content: Content) => content.priceDisplayOption.showVat;

const hasShowCaeData = (content: Content) => content.caeDisplayOption.showCae;

const hasShowConfigureCTA = (content: Content) => content.ctaDisplayOptions.showConfigureCTA;

const discoverIsPrimaryBtn = (content: Content) => content.ctaDisplayOptions.isDiscoverPrimaryBtn;

const hasShowNewCarsCTA = (content: Content) => content.ctaDisplayOptions.showNewCarsCTA;

const hasShowUsedCarsCTA = (content: Content) => content.ctaDisplayOptions.showUsedCarsCTA;

export function filterMockedAndEmptyCarlineGroups(carlineGroups: CarlineGroup[]): CarlineGroup[] {
  const noEmptyCarlineGroups = carlineGroups.filter(({ carlines }) => carlines.length > 0);

  return noEmptyCarlineGroups;
}

export const ContextProvider: FC<ContextProviderProps> = ({
  featureAppId,
  localeService,
  logger,
  trackingService,
  children,
  i18nService,
  contentService,
  renderModeService,
  numberFormatterService,
}: PropsWithChildren<ContextProviderProps>) => {
  const content = useMemo(() => {
    const originalContent = contentService.getContent();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if ('__type' in originalContent && originalContent.__type === 'aem-headless') {
      const mappedContent = mapHeadlessContent(originalContent);
      logger?.info('Feature App created with headless content:', mappedContent);
      return mappedContent;
    }
    logger?.info('Feature App createdwith FACE content:', originalContent);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return originalContent;
  }, []);

  const [isEditMode] = useState<boolean>(renderModeService?.getRenderMode() === RenderMode.EDIT);
  const [showBasePrice, setShowBasePrice] = useState<boolean>(hasShowBasePrice(content));
  const [showVat, setShowVat] = useState<boolean>(hasShowVat(content));

  const [showCaeData, setShowCaeData] = useState<boolean>(hasShowCaeData(content));
  const [showConfigureCTA, setShowConfigureCTA] = useState<boolean>(hasShowConfigureCTA(content));
  const [isDiscoverPrimaryBtn, setIsDiscoverPrimaryBtn] = useState<boolean>(
    discoverIsPrimaryBtn(content),
  );
  const [showNewCarsCTA, setShowNewCarsCTA] = useState<boolean>(hasShowNewCarsCTA(content));
  const [showUsedCarsCTA, setShowUsedCarsCTA] = useState<boolean>(hasShowUsedCarsCTA(content));

  const [priceFootnotes, setPriceFootnotes] = useState<string[] | undefined>(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    content.priceFootnotes,
  );

  const [exploreModelsUrl, setExploreModelsUrl] = useState<string | null>(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    content.quickActions?.exploreModelsUrl,
  );
  const [compareModelsUrl, setCompareModelsUrl] = useState<string | null>(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    content.quickActions?.compareModelsUrl,
  );

  const [selectedCarGroupId, setSelectedCarGroupId] = useState<string | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredCarlineGroups, setFilteredCarlineGroups] = useState<
    MoxxCarlineGroup[] | undefined
  >(undefined);
  const [selectedBodyType, setSelectedBodyType] = useState<string | undefined>(undefined);
  const [selectedVehicleType, setSelectedVehicleType] = useState<string | undefined>(undefined);
  const [selectedFilter, setSelectedFilter] = useState<string>(ALL_MODELS);
  const carlineCardMenuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLUListElement>(null);
  const filterContainer = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let mounted = true;

    const contentChangeListener = () => {
      if (mounted) {
        const newInitialContent = contentService.getContent();
        const newContent = mapHeadlessContent(newInitialContent);
        setShowBasePrice(hasShowBasePrice(newContent));
        setShowVat(hasShowVat(newContent));
        setShowCaeData(hasShowCaeData(newContent));
        setShowConfigureCTA(hasShowConfigureCTA(newContent));
        setIsDiscoverPrimaryBtn(discoverIsPrimaryBtn(newContent));
        setShowNewCarsCTA(hasShowNewCarsCTA(newContent));
        setShowUsedCarsCTA(hasShowUsedCarsCTA(newContent));

        setPriceFootnotes(newContent.priceFootnotes);

        setExploreModelsUrl(newContent.quickActions.exploreModelsUrl);
        setCompareModelsUrl(newContent.quickActions.compareModelsUrl);
      }
    };
    contentService.onContentChange(contentChangeListener);

    return () => {
      mounted = false;
      contentService.removeOnContentChange(contentChangeListener);
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      featureAppId,
      i18nService,
      i18nMessages,
      modalOpenState: [
        isModalOpen,
        {
          openModal: () => {
            setIsModalOpen(true);
          },
          closeModal: () => {
            setIsModalOpen(false);
          },
        },
      ] as const,
      logger,
      trackingService,
      isEditMode,
      localeService,
      numberFormatterService,
      selectedCarGroupId,
      setSelectedCarGroupId,
      filteredCarlineGroups,
      setFilteredCarlineGroups,
      selectedBodyType,
      setSelectedBodyType,
      selectedFilter,
      setSelectedFilter,
      selectedVehicleType,
      setSelectedVehicleType,
      showBasePrice,
      showVat,
      showCaeData,
      showConfigureCTA,
      isDiscoverPrimaryBtn,
      showNewCarsCTA,
      showUsedCarsCTA,
      priceFootnotes,
      exploreModelsUrl,
      compareModelsUrl,
      carlineCardMenuRef,
      containerRef,
      filterContainer,
    }),
    [
      featureAppId,
      i18nService,
      i18nMessages,
      isModalOpen,
      logger,
      trackingService,
      isEditMode,
      localeService,
      numberFormatterService,
      selectedCarGroupId,
      setSelectedCarGroupId,
      selectedVehicleType,
      setSelectedVehicleType,
      filteredCarlineGroups,
      setFilteredCarlineGroups,
      selectedBodyType,
      setSelectedBodyType,
      selectedFilter,
      setSelectedFilter,
      showBasePrice,
      showVat,
      showCaeData,
      showConfigureCTA,
      isDiscoverPrimaryBtn,
      showNewCarsCTA,
      showUsedCarsCTA,
      priceFootnotes,
      exploreModelsUrl,
      compareModelsUrl,
      carlineCardMenuRef,
      containerRef,
      filterContainer,
    ],
  );

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};
export const usePriceFootnotes = () => useContext(Context).priceFootnotes;
export const useFeatureAppId = () => useContext(Context).featureAppId;
export const useLogger = () => useContext(Context).logger;
// eslint-disable-next-line react-hooks/rules-of-hooks
export const isEditMode = () => useContext(Context).isEditMode;
export const useI18nService = () => useContext(Context).i18nService;
export const useTrackingService = () => useContext(Context).trackingService;
export const useModalOpenState = () => useContext(Context).modalOpenState;
export const useNumberFormatter = (nr: number) =>
  useContext(Context).numberFormatterService.formatNumber(nr);

export const useCurrencyFormatter = (nr: number) =>
  useContext(Context).numberFormatterService.formatCurrency(nr);

export const useAppContext = () => {
  const context = React.useContext(Context);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
